import React from 'react';
import { seoConfig } from '../../config/seo';
interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  canonicalUrl?: string;
  noindex?: boolean;
  structuredData?: object;
}
/**
 * SEO Head component for meta tags and structured data
 * Handles all SEO-related head elements
 *
 * @example
 * <SEOHead
 *   title="Pricing Plans"
 *   description="Choose the perfect plan for your needs"
 * />
 */
export function SEOHead({
  title,
  description = seoConfig.defaultDescription,
  keywords = seoConfig.keywords,
  ogImage = seoConfig.ogImage,
  ogType = 'website',
  canonicalUrl,
  noindex = false,
  structuredData
}: SEOHeadProps) {
  const fullTitle = title ? seoConfig.titleTemplate.replace('%s', title) : seoConfig.defaultTitle;
  const canonical = canonicalUrl || seoConfig.siteUrl;
  return <>
      {/* Primary Meta Tags */}
      <title data-id="element-2157">{fullTitle}</title>
      <meta name="title" content={fullTitle} data-id="element-2158" />
      <meta name="description" content={description} data-id="element-2159" />
      <meta name="keywords" content={keywords.join(', ')} data-id="element-2160" />

      {/* Robots */}
      {noindex ? <meta name="robots" content="noindex, nofollow" data-id="element-2161" /> : <meta name="robots" content="index, follow" data-id="element-2162" />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonical} data-id="element-2163" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} data-id="element-2164" />
      <meta property="og:url" content={canonical} data-id="element-2165" />
      <meta property="og:title" content={fullTitle} data-id="element-2166" />
      <meta property="og:description" content={description} data-id="element-2167" />
      <meta property="og:image" content={ogImage} data-id="element-2168" />
      <meta property="og:image:width" content={String(seoConfig.ogImageWidth)} data-id="element-2169" />
      <meta property="og:image:height" content={String(seoConfig.ogImageHeight)} data-id="element-2170" />
      <meta property="og:site_name" content={seoConfig.siteName} data-id="element-2171" />

      {/* Twitter */}
      <meta property="twitter:card" content={seoConfig.twitterCard} data-id="element-2172" />
      <meta property="twitter:url" content={canonical} data-id="element-2173" />
      <meta property="twitter:title" content={fullTitle} data-id="element-2174" />
      <meta property="twitter:description" content={description} data-id="element-2175" />
      <meta property="twitter:image" content={seoConfig.twitterImage} data-id="element-2176" />
      {seoConfig.social.twitter && <meta property="twitter:site" content={seoConfig.social.twitter} data-id="element-2177" />}

      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" data-id="element-2178" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" data-id="element-2179" />
      <meta name="language" content="English" data-id="element-2180" />
      <meta name="author" content={seoConfig.siteName} data-id="element-2181" />

      {/* Structured Data */}
      {structuredData && <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(structuredData)
    }} data-id="element-2182" />}
    </>;
}